import {setTitle} from '@github-ui/document-metadata'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ContextualGlobalSsoBanner} from '@github-ui/global-sso-banner/contextual'
import {VIEW_IDS} from '@github-ui/issue-url-helper/constants/view-constants'
import {IssueViewer} from '@github-ui/issue-viewer/IssueViewer'
import {ISSUE_VIEWER_DEFAULT_CONFIG, type OptionConfig} from '@github-ui/issue-viewer/OptionConfig'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {useLocation} from '@github-ui/react-router'
import type {SubIssueSidePanelItem} from '@github-ui/sub-issues/sub-issue-types'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import {ScreenFullIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {type ReactNode, Suspense, useCallback, useEffect} from 'react'
import {type EntryPointComponent, graphql, type PreloadedQuery, useFragment, usePreloadedQuery} from 'react-relay'

import {DashboardSearch} from '../components/dashboard/DashboardSearch'
import {Header} from '../components/list/header/Header'
import {HeaderLoading} from '../components/list/header/HeaderLoading'
import {IssueSidePanel} from '../components/show/IssueSidePanel'
import type {SavedViewsQuery} from '../components/sidebar/__generated__/SavedViewsQuery.graphql'
import MobileNavigation from '../components/sidebar/MobileNavigation'
import {SavedViewsGraphqlQuery as customViews} from '../components/sidebar/SavedViews'
import {Sidebar} from '../components/sidebar/Sidebar'
import {LABELS} from '../constants/labels'
import {useSetCurrentViewId} from '../contexts/QueryContext'
import {useEntryPointsLoader} from '../hooks/use-entrypoint-loaders'
import {useIssueSidePanelNavigation} from '../hooks/use-issue-side-panel-navigation'
import {useRouteInfo} from '../hooks/use-route-info'
import {IssueRepoNewChoosePage, IssueRepoNewPage} from '../lazy-routes'
import {preloadWhenIdle} from '../utils/preload-when-idle'
import type {ClientSideRelayDataGeneratorViewQuery} from './__generated__/ClientSideRelayDataGeneratorViewQuery.graphql'
import type {IssueDashboardCustomViewPageCurrentViewFragment$key} from './__generated__/IssueDashboardCustomViewPageCurrentViewFragment.graphql'
import type {IssueDashboardCustomViewPageQuery} from './__generated__/IssueDashboardCustomViewPageQuery.graphql'
import type {IssueDashboardCustomViewPageSearchListFragment$key} from './__generated__/IssueDashboardCustomViewPageSearchListFragment.graphql'
import {AnalyticsWrapper} from './AnalyticsWrapper'
import {currentViewQuery as currentView} from './ClientSideRelayDataGenerator'
import styles from './IssueDashboardCustomViewPage.module.css'

const renderViewSlotted = ({
  header,
  filterBar,
  results,
}: {
  header: ReactNode
  filterBar: ReactNode
  results: ReactNode
}) => (
  <div className={styles.slottedList}>
    {header}
    <div className={styles.slottedListBody}>
      <FilteredListPageLayout.FilterBar aria-label="Filter issues">{filterBar}</FilteredListPageLayout.FilterBar>
      <FilteredListPageLayout.Results aria-label="Issues">{results}</FilteredListPageLayout.Results>
    </div>
  </div>
)

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const PageQuery = graphql`
  query IssueDashboardCustomViewPageQuery(
    $query: String = "state:open archived:false assignee:@me sort:updated-desc"
    $first: Int = 25
    $labelPageSize: Int = 20
    $skip: Int = null
    $type: SearchType = ISSUE_ADVANCED
    $showIssueFieldPills: Boolean = false
  ) {
    ...IssueDashboardCustomViewPageSearchListFragment
      @arguments(
        query: $query
        first: $first
        labelPageSize: $labelPageSize
        skip: $skip
        fetchRepository: true
        type: $type
        showIssueFieldPills: $showIssueFieldPills
      )
  }
`

export const IssueDashboardCustomViewPage: EntryPointComponent<
  {
    pageQuery: IssueDashboardCustomViewPageQuery
    currentViewQuery: ClientSideRelayDataGeneratorViewQuery
    customViewsQuery: SavedViewsQuery
  },
  Record<string, never>
> = ({queries: {pageQuery, currentViewQuery, customViewsQuery}}) => {
  const {
    sidePanelItemIdentifier,
    setSidePanelItemIdentifier,
    sidePanelItemURL,
    onCloseSidePanel,
    onParentIssueActivate,
  } = useRouteInfo()
  const {queryRef: pageQueryRef} = useEntryPointsLoader(pageQuery, PageQuery)
  const {queryRef: currentViewRef} = useEntryPointsLoader(currentViewQuery, currentView)
  const {queryRef: customViewsRef} = useEntryPointsLoader(customViewsQuery, customViews)

  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueRepoNewPage.preload()
      IssueRepoNewChoosePage.preload()
    })
  }, [])

  const setCurrentViewId = useSetCurrentViewId()

  const {pathname} = useLocation()
  const id = pathname.split('/').pop()

  const {handleListItemsChange, handleNavigateToNextItem, handleNavigateToPreviousItem} = useIssueSidePanelNavigation({
    sidePanelItemIdentifier,
    setSidePanelItemIdentifier,
  })

  useEffect(() => {
    setCurrentViewId(id ? id : VIEW_IDS.empty)
  }, [id, pageQuery, setCurrentViewId])

  const onSubIssueClick = useCallback(
    (subIssueItem: SubIssueSidePanelItem) => {
      const {owner, repo, number} = subIssueItem
      setSidePanelItemIdentifier({owner, repo, number, type: 'Issue'})
    },
    [setSidePanelItemIdentifier],
  )

  const onIssueNavigate = useCallback(
    (issueIdentifier: {owner: string; repo: string; number: number}) => {
      setSidePanelItemIdentifier({...issueIdentifier, type: 'Issue'})
    },
    [setSidePanelItemIdentifier],
  )

  if (!currentViewRef) return null
  if (!pageQueryRef) return null
  if (!customViewsRef) return null

  const config: Partial<OptionConfig> = {
    shouldSkipSetDocumentTitle: true,
    onClose: onCloseSidePanel,
    insideSidePanel: true,
    showRepositoryPill: true,
    tabbedTimeline: isFeatureEnabled('issues_react_timeline_tabs'),
    onSubIssueClick,
    onIssueNavigate,
    onParentIssueActivate,
    navigateBack: onCloseSidePanel,
    onNavigateToNextItem: handleNavigateToNextItem,
    onNavigateToPreviousItem: handleNavigateToPreviousItem,
    additionalHeaderActions: (
      <IconButton
        as="a"
        role="link"
        variant="invisible"
        icon={ScreenFullIcon}
        aria-label={LABELS.sidePanelTooltip}
        href={sidePanelItemURL}
      />
    ),
  }

  const issueViewerConfig: OptionConfig = Object.assign({}, ISSUE_VIEWER_DEFAULT_CONFIG, config)

  return (
    <>
      <IssueDashboardCustomViewPageContent
        pageQueryRef={pageQueryRef}
        currentViewQueryRef={currentViewRef}
        savedViewsQueryRef={customViewsRef}
        onSidePanelNavigate={setSidePanelItemIdentifier}
        onListItemsChange={handleListItemsChange}
      />
      {sidePanelItemIdentifier && (
        <IssueSidePanel onClose={onCloseSidePanel} optionConfig={issueViewerConfig}>
          <IssueViewer itemIdentifier={sidePanelItemIdentifier} optionConfig={issueViewerConfig} />
        </IssueSidePanel>
      )}
    </>
  )
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const SearchListFragment = graphql`
  fragment IssueDashboardCustomViewPageSearchListFragment on Searchable
  @argumentDefinitions(
    query: {type: "String!"}
    first: {type: "Int"}
    labelPageSize: {type: "Int!"}
    skip: {type: "Int", defaultValue: null}
    fetchRepository: {type: "Boolean!"}
    type: {type: "SearchType", defaultValue: ISSUE_ADVANCED}
    showIssueFieldPills: {type: "Boolean!", defaultValue: false}
  ) {
    ...DashboardSearchFragment
      @arguments(
        query: $query
        first: $first
        labelPageSize: $labelPageSize
        skip: $skip
        fetchRepository: $fetchRepository
        type: $type
        showIssueFieldPills: $showIssueFieldPills
      )
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const CurrentViewFragment = graphql`
  fragment IssueDashboardCustomViewPageCurrentViewFragment on Shortcutable {
    name
    ...HeaderCurrentViewFragment
  }
`

function IssueDashboardCustomViewPageContent({
  pageQueryRef,
  currentViewQueryRef,
  savedViewsQueryRef,
  onSidePanelNavigate,
  onListItemsChange,
}: {
  pageQueryRef: PreloadedQuery<IssueDashboardCustomViewPageQuery>
  currentViewQueryRef: PreloadedQuery<ClientSideRelayDataGeneratorViewQuery>
  savedViewsQueryRef: PreloadedQuery<SavedViewsQuery>
  onSidePanelNavigate?: (issue: ItemIdentifier) => void
  onListItemsChange?: (items: ItemIdentifier[]) => void
}) {
  const {itemIdentifier} = useRouteInfo()
  const data = usePreloadedQuery<IssueDashboardCustomViewPageQuery>(PageQuery, pageQueryRef)
  const currentViewEncapsulated = usePreloadedQuery<ClientSideRelayDataGeneratorViewQuery>(
    currentView,
    currentViewQueryRef,
  )

  const searchListData = useFragment<IssueDashboardCustomViewPageSearchListFragment$key>(SearchListFragment, data)
  const currentViewData = useFragment<IssueDashboardCustomViewPageCurrentViewFragment$key>(
    CurrentViewFragment,
    currentViewEncapsulated.node,
  )

  useEffect(() => {
    if (currentViewData?.name) {
      setTitle(LABELS.documentTitleForView(currentViewData.name))
    }
  }, [currentViewData?.name])

  if (!currentViewEncapsulated.node || !currentViewData || !savedViewsQueryRef) {
    return null
  }

  const filteredListLayoutEnabled = isFeatureEnabled('global_issues_filtered_list_layout')

  const header = (
    <Suspense fallback={<HeaderLoading />}>
      <Header currentViewKey={currentViewData} />
    </Suspense>
  )

  const dashboardSearch = (
    <DashboardSearch
      currentView={currentViewEncapsulated.node}
      onSidePanelNavigate={onSidePanelNavigate}
      onListItemsChange={onListItemsChange}
      itemIdentifier={itemIdentifier}
      search={searchListData}
      showSsoBanner={false}
      renderSlotted={
        filteredListLayoutEnabled
          ? ({filterBar, results}) => renderViewSlotted({header, filterBar, results})
          : undefined
      }
    />
  )

  if (filteredListLayoutEnabled) {
    return (
      <AnalyticsWrapper category="Issues Dashboard">
        <ContextualGlobalSsoBanner />
        <FilteredListPageLayout>
          <FilteredListPageLayout.Sidebar aria-label={LABELS.viewSidebarPaneAriaLabel} dialogTitle="Issues">
            <Sidebar customViewsRef={savedViewsQueryRef} />
          </FilteredListPageLayout.Sidebar>
          <FilteredListPageLayout.Content as="div">{dashboardSearch}</FilteredListPageLayout.Content>
        </FilteredListPageLayout>
        <MobileNavigation customViewsRef={savedViewsQueryRef} />
      </AnalyticsWrapper>
    )
  }

  return (
    <AnalyticsWrapper category="Issues Dashboard">
      <ContextualGlobalSsoBanner />
      <ThreePanesLayout
        leftPaneWidth={'small'}
        leftPane={{
          element: <Sidebar customViewsRef={savedViewsQueryRef} isFullHeight />,
          ariaLabel: LABELS.viewSidebarPaneAriaLabel,
        }}
        middlePane={
          <div className={styles.searchListContainer}>
            {header}
            {dashboardSearch}
          </div>
        }
      />
      <MobileNavigation customViewsRef={savedViewsQueryRef} />
    </AnalyticsWrapper>
  )
}
